// LOGISPRD-12506 — 이 import 세 줄은 `ConsoleShell` 보다 **위**에 있어야 한다. `base.css` 의
// `.moms-console *` 와 콘솔 컴포넌트들의 CSS 모듈 클래스는 특이도가 (0,1,0) 으로 같아서 나중에
// 선언된 쪽이 이긴다 — 순서가 뒤집히면 `font-family: var(--mono)` 를 명시한 컴포넌트가 sans 로
// 떨어진다. `mwms-ds` 시트는 어드민 `App.tsx` 가 이미 불렀지만, 이 파일이 지연 청크의 진입이라
// 여기서도 명시해 두면 청크 단독으로도 전제가 성립한다(번들러가 중복은 제거한다).
import '@musinsa/mwms-ds/style.css'
import '../styles/tokens.css'
import '../styles/base.css'
import { useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { consoleQueryClient } from '@/console/queryClient'
import { ConsoleShell } from './ConsoleShell'
import { ConsoleHeader } from './ConsoleHeader'

/**
 * LOGISPRD-12506 (어드민 편입) — 온콜 콘솔의 실사용 진입점. 어드민 라우트 `/#console`.
 *
 * ## 왜 별도 HTML(`console.html`)이 아니라 어드민 화면인가
 *
 * 배포된 호스트(`*.moms.musinsalogistics.co.kr`)의 CDN 은 `/` 하나만 S3 로 보내고 나머지 경로는
 * 전부 API 오리진으로 보낸다. 그래서 `/console.html` 을 열면 정적 파일이 아니라 게이트웨이의
 * `ERROR_UNAUTHORIZED` JSON 이 뜬다(어드민의 `/gi26` 같은 경로도 직접 치면 같은 결과다 — 사람들이
 * `/` 로 들어와 화면 안에서 클릭으로 이동하기 때문에 드러나지 않았을 뿐이다). CDN 설정을 고치려면
 * SRE 가 환경 4개에 캐시 비헤이비어를 추가해야 해서, 우리 레포 안에서 끝나는 쪽을 택했다.
 *
 * 부수 효과로 로그인 문제도 같이 사라진다. 세션 정보(`AuthStorage.user`)는 `sessionStorage` 라
 * **탭마다 따로**이고, 그것을 채우는 코드는 로그인 콜백 화면 하나뿐이다. 별도 HTML 을 새 탭으로
 * 열면 그 탭의 세션은 비어 있어서, `MOMS_ENG` 권한이 있어도 "권한이 없어요" 화면이 떴다. 어드민
 * 안에서 클라이언트 사이드로 전환하면 같은 탭·같은 세션을 그대로 쓴다.
 *
 * ## 왜 경로가 아니라 해시인가
 *
 * 위와 같은 이유다 — `/console` 같은 경로로 만들면 새로고침·북마크·주소 공유가 전부 그 JSON 으로
 * 떨어진다. 해시는 서버로 전송되지 않아 경로가 항상 `/` 로 유지된다. 판정은 `shell/tabs.ts` 의
 * `isConsoleHash`, 라우팅은 `App.tsx` 의 `ConsoleHashGate` 다.
 *
 * ## QueryClient
 *
 * 콘솔은 어드민과 다른 기본값(`enabled:false` 등)을 쓰는 자기 `QueryClient` 를 갖는다
 * (`console/queryClient.ts`). 어드민의 Provider 를 물려받지 않도록 여기서 한 번 더 감싼다 —
 * 콘솔 훅이 어드민 캐시에 섞이면 조치 결과가 엉뚱한 화면에 캐시된다.
 */
const CONSOLE_FONTS_ID = 'moms-console-fonts'
const CONSOLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Noto+Sans+KR:wght@400;500;700;900&display=swap'

/**
 * 콘솔 본문 전용 웹폰트를 콘솔에 들어왔을 때만 받는다(PR 리뷰 High).
 *
 * `styles/tokens.css` 에 `@import` 로 두면 `format: 'iife'` 번들링 때문에 어드민 전체 CSS 시트
 * 맨 위로 올라가 콘솔에 들어가지 않는 사용자까지 매 로드마다 fonts.googleapis.com 을 친다.
 *
 * 붙인 `<link>` 는 언마운트해도 떼지 않는다 — 콘솔을 다시 열 때 폰트가 없어 깜빡이는 편이
 * `<link>` 하나 남기는 것보다 나쁘다. id 로 중복 주입만 막는다. 실패(사내망에서 Google 도메인
 * 차단 등)해도 `--sans`/`--mono` 의 폴백 스택으로 화면은 정상 동작한다.
 */
const ensureConsoleFonts = (): void => {
  if (document.getElementById(CONSOLE_FONTS_ID)) {
    return
  }

  const link = document.createElement('link')
  link.id = CONSOLE_FONTS_ID
  link.rel = 'stylesheet'
  link.href = CONSOLE_FONTS_HREF
  document.head.appendChild(link)
}

const ConsolePage = (): JSX.Element => {
  useEffect(ensureConsoleFonts, [])

  return (
    <QueryClientProvider client={consoleQueryClient}>
      <ConsoleShell header={<ConsoleHeader />} />
    </QueryClientProvider>
  )
}

export default ConsolePage
